import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HeartPulse, GraduationCap, TrendingUp, Building2, ArrowRight, CalendarCheck, Users, LayoutDashboard, BarChart3, BookOpen, UserCircle, Award, LineChart, CreditCard, Workflow, Wrench, Home } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }
  })
};

const industries = [
  {
    icon: HeartPulse,
    title: 'Healthcare Systems',
    desc: 'We help healthcare providers streamline operations and improve patient experiences through intelligent systems.',
    features: [
      { icon: CalendarCheck, label: 'Appointment booking platforms' },
      { icon: Users, label: 'Patient management systems' },
      { icon: LayoutDashboard, label: 'Admin dashboards' },
      { icon: BarChart3, label: 'Reporting tools' },
    ],
    gradient: 'from-[#B42314] to-[#E65A14]'
  },
  {
    icon: GraduationCap,
    title: 'EdTech Platforms',
    desc: 'We build scalable education systems for modern learning.',
    features: [
      { icon: BookOpen, label: 'Learning management systems' },
      { icon: UserCircle, label: 'Student portals' },
      { icon: Award, label: 'Course platforms' },
      { icon: LineChart, label: 'Progress tracking' },
    ],
    gradient: 'from-[#E65A14] to-[#FF8C28]'
  },
  {
    icon: TrendingUp,
    title: 'Fintech Solutions',
    desc: 'Secure and scalable systems for financial operations.',
    features: [
      { icon: BarChart3, label: 'Data dashboards' },
      { icon: CreditCard, label: 'Transaction systems' },
      { icon: LayoutDashboard, label: 'Reporting tools' },
      { icon: Workflow, label: 'Workflow automation' },
    ],
    gradient: 'from-[#FF8C28] to-[#FFBE50]'
  },
  {
    icon: Building2,
    title: 'Property Management',
    desc: 'Simplifying property operations through smart systems.',
    features: [
      { icon: Users, label: 'Tenant management platforms' },
      { icon: CreditCard, label: 'Payment tracking systems' },
      { icon: Wrench, label: 'Maintenance workflows' },
      { icon: Home, label: 'Owner dashboards' },
    ],
    gradient: 'from-[#E65A14] to-[#FFBE50]'
  },
];

export default function IndustriesPage() {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section data-testid="industries-hero" className="relative py-24 lg:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/9003f248-e35e-4fe8-a9ec-ff2fb801281b/images/7e310cbbba1436184b8111b5990c195f7183273a8d278897e447c99f8640c83d.png)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0C1C] via-[#0A0C1C]/95 to-[#12142B]" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF8C28] mb-4">
              Industries
            </motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white">
              Who We Work With
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="mt-6 text-base sm:text-lg text-[#C8C8C8] leading-relaxed max-w-2xl mx-auto">
              We partner with businesses across key sectors to build intelligent digital systems that solve real-world challenges.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Industry Blocks */}
      <section data-testid="industries-detail" className="py-12 bg-[#0A0C1C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-24">
          {industries.map((ind, i) => (
            <motion.div
              key={ind.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
              data-testid={`industry-detail-${i}`}
              className="relative"
            >
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
                {/* Left: Content */}
                <div className={`lg:col-span-3 ${i % 2 !== 0 ? 'lg:order-2' : ''}`}>
                  <motion.div variants={fadeUp} custom={0} className="flex items-center gap-4 mb-6">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${ind.gradient} bg-opacity-20 flex items-center justify-center`} style={{ background: `linear-gradient(135deg, ${ind.gradient.includes('B42314') ? '#B42314' : '#E65A14'}20, ${ind.gradient.includes('FFBE50') ? '#FFBE50' : '#FF8C28'}10)` }}>
                      <ind.icon size={28} className="text-[#FF8C28]" />
                    </div>
                    <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tight text-white">
                      {ind.title}
                    </h2>
                  </motion.div>
                  <motion.p variants={fadeUp} custom={1} className="text-[#C8C8C8] leading-relaxed mb-8">
                    {ind.desc}
                  </motion.p>
                  <motion.div variants={fadeUp} custom={2} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {ind.features.map(f => (
                      <div key={f.label} className="flex items-center gap-3 p-3 rounded-lg bg-[#12142B]/80 border border-[#FF8C28]/10 hover:border-[#FF8C28]/30 transition-colors">
                        <div className="w-8 h-8 rounded-md bg-[#FF8C28]/10 flex items-center justify-center flex-shrink-0">
                          <f.icon size={16} className="text-[#FF8C28]" />
                        </div>
                        <span className="text-sm text-[#C8C8C8]">{f.label}</span>
                      </div>
                    ))}
                  </motion.div>
                </div>
                {/* Right: Visual */}
                <motion.div
                  variants={fadeUp}
                  custom={2}
                  className={`lg:col-span-2 ${i % 2 !== 0 ? 'lg:order-1' : ''}`}
                >
                  <div className="aspect-square rounded-2xl bg-gradient-to-br from-[#12142B] to-[#0A0C1C] border border-[#FF8C28]/15 flex items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#E65A14]/5 via-transparent to-[#FF8C28]/3" />
                    <ind.icon size={100} className="text-[#FF8C28]/8" />
                  </div>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-b from-[#0A0C1C] to-[#12142B]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.h2 variants={fadeUp} custom={0} className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white">
              Don't see your industry?
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="mt-4 text-[#C8C8C8]">
              We work across sectors. Let's discuss how we can build something for your business.
            </motion.p>
            <motion.div variants={fadeUp} custom={2} className="mt-8">
              <Link
                to="/contact"
                data-testid="industries-cta-btn"
                className="inline-flex items-center px-10 py-4 text-sm font-bold tracking-wide text-white bg-gradient-to-r from-[#E65A14] to-[#FF8C28] rounded-sm hover:shadow-[0_0_30px_rgba(230,90,20,0.5)] transition-all duration-300"
              >
                Get in Touch
                <ArrowRight size={16} className="ml-2" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
